import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, queryAssignedNodes } from 'lit/decorators.js';
import { style } from './zui-radio-css.js';
import { classMap } from 'lit/directives/class-map.js';
import type { PropertyValues } from 'lit';

/**
 * Radio form control, insert into `<zui-radio-group>` to ensure only one `<zui-radio>` is chosen at a time.
 * @element zui-radio
 *
 * @slot - (required): Default, unnamed slot; used for inserting label text into `<zui-radio>`
 * @slot icon - Insert an icon into the label container; appears before the default, unnamed slotted label text
 *
 * @attr {string} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 *
 * @cssprop [--zui-radio-color=-var(-zui-blue)] - (all): radio fill color when `checked`
 * @cssprop [--zui-radio-hover-color=var(--zui-blue-400)] - (all); hover state radio fill color
 * @cssprop [--zui-radio-animate-color=var(--zui-blue-300)] - (all): animation state after a click, radio fill color
 * @cssprop [--zui-radio-indicator-margin=0.6875rem 0.6875rem 0.6875rem 0 (11px 11px 11px 0)] - (default): radio marginal positioning
 * @cssprop [--zui-radio-label-margin=0.5625rem 1.25rem 0.5625rem 0 (9px 20px 9px 0)] - (default): radio label marginal positioning
 * @cssprop [--zui-radio-indicator-margin=0.6875rem (11px)] - (default): focus state rounded rectangle that appears, changes the `position:` `absolute` `top` value. If CSS custom props affecting margins are changed then this likely needs to be updated to adjust focus ring to be centered.
 * @cssprop [--zui-radio-gallery-width=8.125rem (130px)] - (`gallery`): width of a `gallery` type `zui-radio`
 *
 * @event {CustomEvent} keydown - Event dispatches when a `keydown` event happens while a `zui-radio` is focused; contains details about which `keycode` was pressed
 * @event {CustomEvent} selected - Event is dispatched when `selected` state changes; contains details about which `identifier` was changed
 * @event {CustomEvent} change - Event is dispatched when `selected` state changes; contains details about which `value` was changed
 */
export class ZuiRadio extends ZuiFormAssociatedElement {
  #initialState: boolean | undefined = undefined;
  #instantiated: boolean = false;
  #checkedChanged: boolean = false;
  #hasIconContent = false;

  @queryAssignedNodes({ slot: 'icon' })
  private _iconSlottedNodes: Array<Node>;

  get _focusControlSelector(): string {
    return '.radio-container';
  }

  get _formValue() {
    return this.checked ? this.value : null;
  }

  /**
   * The value assigned to the radio button, when form is submitted is included along with `name`
   */
  @property({ type: String })
  value = 'on';

  /**
   * Current state, whether or not this radio is `checked`
   */
  @property({ type: Boolean, reflect: true })
  checked = false;

  static get styles() {
    return [super.styles, style];
  }

  /**
   * (deprecated!): use `value` instead
   */
  @property({ type: String })
  get identifier() {
    return this.value;
  }

  set identifier(val) {
    this.value = val;
  }

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('role', 'radio');
    // we want to go a little faster than LitElement and behave more like native HTML Form Associated Elements
    const initialChecked = this.hasAttribute('checked');
    const initialValue = this.getAttribute('value');
    this.#initialState = initialChecked;
    this._setFormValue(initialChecked ? initialValue : null);
    this.addEventListener('click', this.#onClick);
  }

  protected formResetCallback() {
    this.checked = this.#initialState;
    this._setFormValue(this._formValue);
  }

  #setOrRemoveCheckedAttribute() {
    this.checked ? this.setAttribute('aria-checked', 'true') : this.setAttribute('aria-checked', 'false');
    this.checked ? this.setAttribute('tabindex', '0') : this.removeAttribute('tabindex');
  }

  #setOrRemoveDisabledAttribute() {
    this.disabled ? this.setAttribute('aria-disabled', 'true') : this.setAttribute('aria-disabled', 'false');
  }

  #selectRadio() {
    this.checked = true;
    this.setAttribute('tabindex', '0');
    this.focus();
    this.dispatchEvent(new CustomEvent('selected', { bubbles: true, composed: true, detail: this.identifier }));
    this.dispatchEvent(new CustomEvent('change', { detail: this.value, bubbles: true }));
  }

  update(changedProps: PropertyValues<this>) {
    super.update(changedProps);
    if (changedProps.has('disabled')) {
      this.#setOrRemoveDisabledAttribute();
    }

    if (changedProps.has('checked')) {
      this.#setOrRemoveCheckedAttribute();
    }

    if (changedProps.has('checked') && this.#instantiated) {
      this.#checkedChanged = true;
      this.requestUpdate();
    }

    if (changedProps.has('checked') || changedProps.has('value')) {
      this._setFormValue(this._formValue);
    }
  }

  firstUpdated(changedProps: PropertyValues<this>) {
    super.firstUpdated(changedProps);

    this.#instantiated = true;
    this.requestUpdate();
  }

  render() {
    return html`
      <section class="radio-container" @click="${this.#onClick}" @keydown="${this.#onKeydown}">
        <div class="radio ${classMap({ 'checked-change': this.#checkedChanged })}">
          <div class="radio-indicator-outline"> <div class="radio-indicator"> </div> </div>
          <div class="animate"></div>
        </div>
        <div class="label">
          <div class="${classMap({ icon: true, hide: !this.#hasIconContent })}"
            ><slot name="icon" @slotchange="${this.#toggleIcon}"></slot
          ></div>
          <div class="label-text"><slot></slot></div>
        </div>
      </section>
    `;
  }

  #onClick() {
    if (!this.disabled && !this.checked) {
      this.#selectRadio();
    }
  }

  #toggleIcon() {
    this.#hasIconContent = this._iconSlottedNodes.length > 0;
    this.requestUpdate();
  }

  #onKeydown(e: KeyboardEvent) {
    const arrowKeyCodes: string[] = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'];
    const spaceKeyCode: string = 'Space';
    const keyCode: string = e.key;
    if (arrowKeyCodes.includes(keyCode)) {
      this.dispatchEvent(new CustomEvent('keydown', { bubbles: true, composed: true, detail: keyCode }));
      e.preventDefault();
      e.stopPropagation();
    }
    if (keyCode === spaceKeyCode) {
      if (!this.checked) {
        this.#selectRadio();
      }
      e.preventDefault();
      e.stopPropagation();
    }
  }
}

window.customElements.define('zui-radio', ZuiRadio);

declare global {
  interface HTMLElementTagNameMap {
    'zui-radio': ZuiRadio;
  }
}
